import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { RefreshCw, ChevronDown, AlertCircle } from 'lucide-react';
import type { RiskCache, RiskFacet } from '../types';
import { getRiskBand, getRiskBandDisplay, formatRiskScore, DEFAULT_ALERT_THRESHOLD } from '../lib/riskBand';
import { useUserStore } from '../store/userStore';

interface RiskEvaluationProps {
  risk: RiskCache | undefined;
  isLoading: boolean;
  isRefreshing?: boolean;
  canRefresh?: boolean;
  /** Show compact summary only — no facet breakdown */
  compact?: boolean;
  onRefresh?: () => void;
}

function useAlertThreshold(): number {
  return useUserStore((s) => s.sessionConfig?.alertThreshold) ?? DEFAULT_ALERT_THRESHOLD;
}

// Score → bar fill color. Derived from the shared band so the bar always
// agrees with the High/Medium/Low label shown next to it.
function scoreBarColor(score: number, alertThreshold: number): string {
  const band = getRiskBand(score, alertThreshold);
  if (band === 'high') return 'bg-red-600';
  if (band === 'medium') return 'bg-amber-400';
  if (band === 'low') return 'bg-green-500';
  return 'bg-gray-300 dark:bg-gray-600';
}

function ScoreBar({ score, slim = false }: { score: number; slim?: boolean }) {
  const alertThreshold = useAlertThreshold();
  const percent = (score / 5) * 100;
  return (
    <div className={`${slim ? 'h-1.5' : 'h-2'} bg-black/10 dark:bg-white/10 rounded-full overflow-hidden flex-1`}>
      <motion.div
        className={`h-full rounded-full ${scoreBarColor(score, alertThreshold)}`}
        initial={{ width: 0 }}
        animate={{ width: `${percent}%` }}
        transition={{ duration: 0.55, ease: 'easeOut' }}
      />
    </div>
  );
}

function FacetRow({ facet }: { facet: RiskFacet }) {
  const [expanded, setExpanded] = useState(false);
  const alertThreshold = useAlertThreshold();
  const { label, bgClass } = getRiskBandDisplay(facet.score, alertThreshold);

  return (
    <div className="border-b border-black/[0.06] dark:border-white/[0.06] last:border-0">
      <button
        onClick={() => setExpanded((p) => !p)}
        className="w-full flex items-center gap-3 py-2.5 text-left hover:bg-black/[0.02] dark:hover:bg-white/[0.03] transition-colors rounded px-1"
      >
        <span className="text-body text-gray-800 dark:text-gray-200 w-36 flex-shrink-0 font-medium capitalize">
          {facet.name}
        </span>
        <ScoreBar score={facet.score} slim />
        <span
          title={`Score: ${formatRiskScore(facet.score)} / 5.0`}
          className={`inline-flex items-center text-micro font-bold px-2 py-0.5 rounded-full flex-shrink-0 cursor-default ${bgClass}`}
        >
          {label}
        </span>
        <ChevronDown
          className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${expanded ? 'rotate-180' : ''}`}
        />
      </button>

      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="overflow-hidden"
          >
            <div className="px-5 pb-3 space-y-4">
              <p className="text-meta text-gray-500 dark:text-gray-400 leading-relaxed">
                {facet.reasoning}
              </p>
              {(facet.signals?.length ?? 0) > 0 && (
                <ul className="space-y-1">
                  {facet.signals?.map((signal, i) => (
                    <li key={i} className="text-meta text-gray-500 dark:text-gray-500 flex items-start gap-1.5">
                      <span className="text-primary mt-0.5 flex-shrink-0">•</span>
                      {signal}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function Skeleton() {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div className="skeleton h-14 w-24 rounded-xl" />
        <div className="flex-1 space-y-4">
          <div className="skeleton h-4 w-28 rounded" />
          <div className="skeleton h-2.5 w-full rounded-full" />
        </div>
      </div>
      {[...Array(6)].map((_, i) => (
        <div key={i} className="flex items-center gap-3">
          <div className="skeleton h-3 w-24 rounded" />
          <div className="skeleton h-2 flex-1 rounded-full" />
          <div className="skeleton h-5 w-10 rounded-full" />
        </div>
      ))}
    </div>
  );
}

export function RiskEvaluation({
  risk,
  isLoading,
  isRefreshing = false,
  canRefresh = false,
  compact = false,
  onRefresh,
}: RiskEvaluationProps) {
  const alertThreshold = useAlertThreshold();

  if (isLoading) {
    return (
      <div className="glass-card rounded-xl p-5">
        <Skeleton />
      </div>
    );
  }

  if (!risk || risk.overallScore == null) {
    return (
      <div className="glass-card rounded-xl p-5 flex items-center gap-3 text-gray-500">
        <AlertCircle className="h-4 w-4 flex-shrink-0" />
        <span className="text-meta">No risk data yet</span>
        {canRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="ml-auto flex items-center gap-1.5 text-meta text-primary hover:text-primary/80 transition-colors disabled:opacity-50 font-medium"
          >
            <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
            Run assessment
          </button>
        )}
      </div>
    );
  }

  const { label, bgClass, scoreLabel } = getRiskBandDisplay(risk.overallScore, alertThreshold);

  // ── Compact view ────────────────────────────────────────────────────────────
  if (compact) {
    const topFacets = [...(risk.facets ?? [])]
      .sort((a, b) => b.score - a.score)
      .slice(0, 2);

    return (
      <div className="glass-card rounded-xl overflow-hidden">
        <div className="px-5 pt-4 pb-3">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-title text-gray-900 dark:text-white">Project Health</h3>
            {risk.isStale && (
              <span className="text-micro text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/20 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-900/30">
                stale
              </span>
            )}
          </div>

          <div className="flex items-center gap-3 mb-3">
            <span
              title={`Score: ${scoreLabel} / 5.0`}
              className={`inline-flex items-center text-2xl font-bold font-display leading-none px-3 py-1.5 rounded-xl cursor-default ${bgClass}`}
            >
              {label}
            </span>
            <p className="text-meta text-gray-400">Hover for score</p>
          </div>

          <ScoreBar score={risk.overallScore} />
        </div>

        {topFacets.length > 0 && (
          <div className="border-t border-black/[0.06] dark:border-white/[0.06] px-5 py-3 space-y-4">
            <p className="text-meta font-semibold uppercase tracking-wider text-gray-400 mb-1.5">Top risk areas</p>
            {topFacets.map((f) => {
              const fc = getRiskBandDisplay(f.score, alertThreshold);
              return (
                <div key={f.name} className="flex items-center gap-2.5">
                  <span className="text-meta text-gray-500 dark:text-gray-400 w-28 flex-shrink-0 capitalize">{f.name}</span>
                  <ScoreBar score={f.score} slim />
                  <span
                    title={`Score: ${fc.scoreLabel} / 5.0`}
                    className={`inline-flex items-center text-micro font-bold px-2 py-0.5 rounded-full flex-shrink-0 cursor-default ${fc.bgClass}`}
                  >
                    {fc.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Full view ────────────────────────────────────────────────────────────────
  return (
    <div className="glass-card rounded-xl overflow-hidden">
      {/* Header */}
      <div className="flex items-start justify-between px-5 pt-4 pb-3">
        <div>
          <h3 className="text-title text-gray-900 dark:text-white">Risk Assessment</h3>
          <p className="text-meta text-gray-400 mt-0.5">
            {new Date(risk.generatedAt).toLocaleString()}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {risk.isStale && (
            <span className="text-micro text-amber-600 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/20 px-2 py-0.5 rounded-full border border-amber-200 dark:border-amber-900/30">
              stale
            </span>
          )}
          {canRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="flex items-center gap-1.5 text-meta text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          )}
        </div>
      </div>

      {/* Score hero */}
      <div className="px-5 pb-4">
        <div className="flex items-center gap-3 mb-2.5">
          <span
            title={`Score: ${scoreLabel} / 5.0`}
            className={`inline-flex items-center text-3xl font-bold font-display leading-none px-4 py-2 rounded-xl cursor-default ${bgClass}`}
          >
            {label}
          </span>
          <p className="text-meta text-gray-400">Hover for score</p>
        </div>
        <ScoreBar score={risk.overallScore} />
      </div>

      {/* Facets */}
      <div className="border-t border-black/[0.06] dark:border-white/[0.06] px-4">
        {(risk.facets ?? []).map((facet) => (
          <FacetRow key={facet.name} facet={facet} />
        ))}
      </div>
    </div>
  );
}
